/* eslint-disable */
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Chart from "react-apexcharts";
import "./Styling/KpiDashboard.css";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Button, Tooltip } from "@material-ui/core";

const KpiDashboard = () => {
    const [data, setData] = useState([]);
    const [analysis, setAnalysis] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const history = useHistory();
    const kpiId = window.location.pathname.split("/").pop();


    useEffect(() => {
        if (parseInt(kpiId) === 152) {
            const fetchExcel = async () => {
                try {
                    const res = await fetch(
                        process.env.PUBLIC_URL + "/js/Gender Diversity Dataset for KPIs.xlsx"
                    );
                    const arrayBuffer = await res.arrayBuffer();
                    const workbook = XLSX.read(arrayBuffer, { type: "array" });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(sheet);
                    setData(jsonData);
                    calculateAnalysis(jsonData);
                } catch (err) {
                    console.error("❌ Error reading Excel file:", err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchExcel();
        } else {
            setIsLoading(false);
        }
    }, [kpiId]);

    const excelDateToMonth = (serial) => {
        const utc_days = Math.floor(serial - 25569);
        const utc_value = utc_days * 86400;
        const date_info = new Date(utc_value * 1000);
        return date_info.toLocaleDateString("en-US", {
            month: "short",
            year: "2-digit",
        });
    };

    const calculateAnalysis = (rows) => {
        if (!rows?.length) return;

        const ratios = rows.map((r) => parseFloat(r["Gender Diversity Ratio"]) || 0);
        const baseline = ratios[0];
        const target = baseline * 2;
        const latest = ratios[ratios.length - 1];
        const monthsElapsed = ratios.length;

        const targetSlope = ((target - baseline) / baseline / 12) * 100;
        const currentSlope = ((latest - baseline) / baseline / monthsElapsed) * 100;
        const lagPercent = ((targetSlope - currentSlope) / targetSlope) * 100;
        const predictedEndRatio =
            latest + ((target - latest) / (12 - monthsElapsed)) * (12 - monthsElapsed);
        const status = currentSlope < targetSlope ? "Lagging" : "On Track";

        setAnalysis({
            baseline,
            target,
            latest,
            monthsElapsed,
            targetSlope,
            currentSlope,
            lagPercent,
            predictedEndRatio,
            status,
        });
    };

    const chartSeries = [
        {
            name: "Actual Gender Ratio",
            data: data.map((r) => parseFloat(r["Gender Diversity Ratio"]) || 0),
        },
        {
            name: "Target (100% Growth)",
            data: data.map((_, idx) => {
                if (!data[0]) return 0;
                const baseline = parseFloat(data[0]["Gender Diversity Ratio"]) || 0;
                const target = baseline * 2;
                const slope = (target - baseline) / 12;
                return baseline + slope * (idx + 1);
            }),
        },
    ];

    const chartOptions = {
        chart: {
            type: "line",
            height: 400,
            toolbar: { show: false },
            animations: { enabled: true, easing: "easeinout", speed: 1000 },
        },
        stroke: {
            curve: "smooth",
            width: [3, 3],
            dashArray: [0, 5],
        },
        markers: {
            size: 5,
            colors: ["#1E3A8A", "#F57C00"],
            strokeWidth: 2,
            hover: { size: 7 },
        },
        grid: { borderColor: "#e0e0e0", strokeDashArray: 5 },
        xaxis: {
            categories: data.map((r) => {
                const raw = r["Time Period"];
                if (!raw) return "";
                return isNaN(raw) ? raw : excelDateToMonth(raw);
            }),
            title: { text: "Month" },
            labels: { rotate: -45, style: { fontSize: "12px", colors: "#555" } },
        },
        yaxis: {
            title: { text: "Gender Diversity Ratio (%)" },
            labels: { formatter: (val) => val.toFixed(2) + "%" },
        },
        colors: ["#1E3A8A", "#F57C00"],
        fill: { type: ["solid", "solid"], opacity: [1, 1] },
        legend: { position: "top", horizontalAlign: "center", fontSize: "14px" },
        tooltip: { theme: "light", y: { formatter: (val) => val.toFixed(2) + "%" } },
    };

    if (isLoading) {
        return (
            <div className="kpi-dashboard-container text-center mt-5">
                <p>Loading KPI Dashboard...</p>
            </div>
        );
    }

    if (parseInt(kpiId) !== 152) {
        return (
            <div className="kpi-dashboard-container text-center mt-5">
                <Tooltip title="Back">
                    <Button
                        variant="outlined"
                        color="default"
                        onClick={() => history.push("/kpi-setting")}
                        style={{ display: "flex", alignItems: "center", textTransform: "none", marginBottom: 20 }}
                    >
                        <ArrowBackIcon style={{ marginRight: 5 }} /> Back
                    </Button>
                </Tooltip>

                <h3>⚙️ KPI Configuration Required</h3>
                <p>
                    This KPI (ID: <strong>{kpiId}</strong>) doesn’t have a linked dataset yet.
                </p>
                <p>Please upload the KPI data and select the correct template to visualize analytics here.</p>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => history.push(`/kpi-setting/${kpiId}`)}
                    style={{ marginTop: 20 }}
                >
                    Configure KPI Data
                </Button>
            </div>
        );
    }

    return (
        <div className="kpi-dashboard-container">
            {/* Header */}
            <div className="kpi-toolbar d-flex align-items-center justify-content-between mb-3">
                <Tooltip title="Back">
                    <Button
                        variant="outlined"
                        color="default"
                        onClick={() => history.push("/kpi-setting")}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            textTransform: "none",
                        }}
                    >
                        <ArrowBackIcon style={{ marginRight: 5 }} /> Back
                    </Button>
                </Tooltip>

                <div className="text-center flex-grow-1">
                    <h3>📊 Gender Diversity KPI Dashboard (FY 2024 – 2025)</h3>
                    <p className="subtitle mb-0">
                        Monthly Gender Diversity Ratio vs Target (100% Growth)
                    </p>
                </div>
                <div style={{ width: "80px" }}></div>
            </div>

            <div className="kpi-dashboard-content">
                {/* Left Panel */}
                <div className="left-panel shadow-sm">
                    <h5 className="section-title">Summary</h5>
                    {analysis ? (
                        <div className="summary-card">
                            <p><strong>Diversity Ratio:</strong> {analysis.baseline.toFixed(2)}%</p>
                            <p><strong>Financial Year:</strong> FY 2024–2025</p>
                            <p><strong>Total Employees:</strong> 721</p>
                            <p><strong>Female Employees:</strong> 16</p>
                            <hr />
                            <p><strong>Target Growth Rate:</strong> 100%</p>
                            <p><strong>Audit Frequency:</strong> Quarterly</p>
                            <p><strong>Duration:</strong> Apr 25 – Mar 26</p>
                        </div>
                    ) : (
                        <p>Loading summary…</p>
                    )}
                </div>

                {/* Center Chart */}
                <div className="center-panel shadow-sm">
                    {data.length > 0 ? (
                        <Chart options={chartOptions} series={chartSeries} type="line" height={400} />
                    ) : (
                        <p>Loading chart…</p>
                    )}
                </div>

                {/* Right Panel */}
                <div className="right-panel shadow-sm">
                    <h5 className="section-title">Analysis</h5>
                    {analysis ? (
                        <div className="analysis-card">
                            <p><strong>Baseline Ratio:</strong> {analysis.baseline.toFixed(2)}%</p>
                            <p><strong>Topline Ratio:</strong> {analysis.target.toFixed(2)}%</p>
                            <p><strong>Duration:</strong> 12 months</p>
                            <hr />
                            <p><strong>Target Slope:</strong> {analysis.targetSlope.toFixed(2)}% / month</p>
                            <p><strong>Current Slope:</strong> {analysis.currentSlope.toFixed(2)}% / month</p>
                            <p><strong>Predicted End Ratio:</strong> {analysis.predictedEndRatio.toFixed(2)}%</p>
                            <p>
                                <strong>Status:</strong>{" "}
                                <span className={`status-badge ${analysis.status.toLowerCase()}`}>
                                    {analysis.status}
                                </span>
                            </p>
                            <p><strong>Lagging by:</strong> {analysis.lagPercent.toFixed(2)}%</p>
                        </div>
                    ) : (
                        <p>Calculating analysis…</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KpiDashboard;
